import type { ContractDocument } from "./contractContent";

/**
 * STABLE CLAUSE + BLOCK IDENTITY (CLOS-1233 — DOCX Kanonu §11).
 *
 * ### ⚠ `Madde 7` bir kimlik DEĞİLDİR
 *
 * Kanon §11.1 ayrımı net: görünen numara sıraya, kimlik belgeye aittir. Öne bir madde
 * eklendiğinde numara kayar, kimlik kaymaz. Kimliğin bağladığı şeyler (yorum çapası,
 * müzakere hedefi, revizyon farkı, AI bulgusu, bildirim derin bağlantısı) sıraya
 * bağlanamaz: sıra kullanıcı yazdıkça değişen bir şeydir.
 *
 * ### ⚠ Bu fonksiyon SUNUCUDA, istemciye ek olarak koşuyor
 *
 * İstemci kimliği taşıyor; ama kimliğin varlığını istemcinin doğru davranmasına
 * bırakamayız. Eski bir sekme, başka bir istemci ya da doğrudan API çağrısı kimliksiz
 * belge gönderebilir — ve o belge kaydedilirse ona çapa atan her şey adressiz kalır.
 *
 * ### ⚠ Kimlik metinden TÜRETİLMİYOR
 *
 * Metin hash'inden kimlik üretmek cazip görünüyor (kendiliğinden kararlı) ama sözleşmelerde
 * tekrar eden cümle kuraldır — *"Bu madde saklıdır."* iki kez geçtiğinde iki blok aynı
 * adresi paylaşırdı. Üstelik metni düzenlemek kimliği değiştirirdi; oysa kimliğin tüm
 * amacı metin değişse de aynı kalmak.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function gecerliMi(deger: unknown, gorulen: Set<string>): deger is string {
  return typeof deger === "string" && UUID.test(deger) && !gorulen.has(deger.toLowerCase());
}

/**
 * Belgedeki eksik/geçersiz/çakışan kimlikleri tamamlar; geçerli olanlara DOKUNMAZ.
 *
 * ⚠ Çakışmada İLK gelen kimliğini korur. Kopyala-yapıştır aynı kimliği ikinci kez
 * getirebiliyor; ikincisini yenilemek, çapası zaten olan bloğu bozmamak demek.
 */
/**
 * ⚠ ÖNCEKİ BELGEDEN KİMLİK DEVRALMA — dar ve KASITLI olarak dar.
 *
 * Kimlik göndermeyen bir istemci (eski sekme, doğrudan API çağrısı, betik) aynı metni
 * ikinci kez kaydettiğinde her seferinde YENİ kimlik alırdı. Bedeli ölçüldü: içerik
 * özeti kimliği de kapsadığı için *"metin değişmedi, yeni versiyon üretme"* kapısı
 * (§16.3) açık kalıyordu — ve içeriği aynı her yeni versiyon iç onay zincirini baştan
 * başlatıp dışa gönderim yetkisini sıfırlıyor.
 *
 * ⚠ Devralma YALNIZ metin BİREBİR AYNIYSA yapılıyor. Konuma bakıp körlemesine devralsaydık,
 * kullanıcı bir paragrafı değiştirdiğinde eski kimlik yeni metne yapışır ve ona asılı yorum
 * SESSİZCE başka bir cümleyi işaret ederdi. Kanon §11.4'ün yasağı tam olarak bu: belirsiz
 * eşleşmede eski kimliği sessizce yanlış hedefe bağlamak. Metin değiştiyse kimlik
 * yenileniyor — çapa GÜRÜLTÜLÜ biçimde kopuyor, sessizce yanlış yere bağlanmıyor.
 */
function devralinanKimlik(
  onceki: ContractDocument | undefined,
  maddeId: unknown,
  blokIndex: number,
  metin: string,
): string | undefined {
  if (!onceki || typeof maddeId !== "string") return undefined;
  const madde = onceki.clauses.find((m) => m.id === maddeId);
  const blok = madde?.blocks[blokIndex] as { id?: string; text?: string } | undefined;
  if (!blok || blok.text !== metin) return undefined;
  return blok.id;
}

export function kimlikleriTamamla(
  document: ContractDocument,
  uret: () => string = () => crypto.randomUUID(),
  onceki?: ContractDocument,
): ContractDocument {
  /**
   * ⚠ MEVCUT kimlikler ÖNCE toplanıyor — ölçülerek eklendi.
   *
   * İlk yazımda kimlikler tek geçişte veriliyordu ve test bunu düşürdü: taze üretilen bir
   * kimlik, belgenin DAHA İLERİSİNDEKİ bir bloğun mevcut kimliğiyle çakıştığında, o blok
   * "çakışan ikinci" sayılıp kimliğini KAYBEDİYORDU. Yani yeni bir blok eklemek, alakasız
   * bir bloğun çapasını koparabiliyordu — kartın engellemek için var olduğu kusurun ta
   * kendisi. Mevcut kimlikler artık konumdan bağımsız olarak korunuyor.
   */
  const mevcutlar = new Set<string>();
  for (const madde of document.clauses) {
    if (typeof madde.id === "string" && UUID.test(madde.id)) mevcutlar.add(madde.id.toLowerCase());
    for (const blok of madde.blocks) {
      const id = (blok as { id?: unknown }).id;
      if (typeof id === "string" && UUID.test(id)) mevcutlar.add(id.toLowerCase());
    }
  }

  if (onceki) {
    for (const madde of onceki.clauses) {
      for (const blok of madde.blocks) {
        const id = (blok as { id?: unknown }).id;
        if (typeof id === "string" && UUID.test(id)) mevcutlar.add(id.toLowerCase());
      }
    }
  }

  const gorulen = new Set<string>();

  const kimlikVer = (mevcut: unknown): string => {
    if (gecerliMi(mevcut, gorulen)) {
      gorulen.add(mevcut.toLowerCase());
      return mevcut;
    }
    let yeni = uret();
    while (gorulen.has(yeni.toLowerCase()) || mevcutlar.has(yeni.toLowerCase())) yeni = uret();
    gorulen.add(yeni.toLowerCase());
    return yeni;
  };

  return {
    clauses: document.clauses.map((madde) => ({
      ...madde,
      id: kimlikVer(madde.id),
      blocks: madde.blocks.map((blok, bi) => ({
        ...blok,
        id: kimlikVer(
          (blok as { id?: unknown }).id ?? devralinanKimlik(onceki, madde.id, bi, blok.text),
        ),
      })),
    })),
  };
}
